import { readQcCorrection } from "./qcCorrection.js";
import { MFG_FINAL_QC_ROLE, MFG_QC_ROLE } from "../roles/turboManufacturingRoles.js";

export type FinalQcPass = {
  status: "passed";
  at: string;
  by: string;
  initials: string;
};

export type FinalCertMark = {
  at: string;
  by: string;
  by_label: string;
};

const PLATFORM_FINAL_QC = new Set(["platform_owner", "platform_admin", "platform_dev"]);

/** Final sign-off is a separate seat from regular QC. Platform roles match the client role check. */
export function canPerformFinalQc(roles: readonly string[]): boolean {
  for (const role of roles) {
    const token = role.trim().toLowerCase();
    if (token === MFG_FINAL_QC_ROLE || PLATFORM_FINAL_QC.has(token)) return true;
  }
  return false;
}

/** Regular QC marks a finished inspection ready for the final QC seat. */
export function canMarkReadyForFinalCert(roles: readonly string[]): boolean {
  for (const role of roles) {
    const token = role.trim().toLowerCase();
    if (token === MFG_QC_ROLE || PLATFORM_FINAL_QC.has(token)) return true;
  }
  return false;
}

export function normalizeFinalQcInitials(value: string): string | null {
  const letters = value.trim().toUpperCase().replace(/[^A-Z]/g, "");
  if (letters.length < 2 || letters.length > 4) return null;
  return letters;
}

export function finalQcSignoff(initials: string): string {
  return `Final QC Passed - Checked by ${initials}`;
}

function readMark(raw: unknown): FinalCertMark | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.at !== "string" || typeof row.by !== "string") return null;
  return {
    at: row.at,
    by: row.by,
    by_label: typeof row.by_label === "string" ? row.by_label : "",
  };
}

export function readReadyForFinalCert(summary: Record<string, unknown> | null | undefined): FinalCertMark | null {
  return readMark(summary?.ready_for_final_cert);
}

export function readFinalCertHistory(summary: Record<string, unknown> | null | undefined): FinalCertMark[] {
  const raw = summary?.final_cert_history;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    const mark = readMark(row);
    return mark ? [mark] : [];
  });
}

/** Keep who marked it ready, and clear the current ticket so QC fixes the new failures. */
export function bounceReadyForFinalCert(summary: Record<string, unknown>): Record<string, unknown> {
  const current = readReadyForFinalCert(summary);
  if (!current) return summary;
  const next: Record<string, unknown> = {
    ...summary,
    final_cert_history: [...readFinalCertHistory(summary), current],
  };
  delete next.ready_for_final_cert;
  delete next.final_qc_alerted_at;
  return next;
}

export function readFinalQc(summary: Record<string, unknown> | null | undefined): FinalQcPass | null {
  const raw = summary?.final_qc;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (row.status !== "passed") return null;
  return {
    status: "passed",
    at: typeof row.at === "string" ? row.at : "",
    by: typeof row.by === "string" ? row.by : "",
    initials: typeof row.initials === "string" ? row.initials : "",
  };
}

/** Clean QC submitted before this instant stays out of the final QC queue. Midnight US Eastern, 22 Oct 2026. */
export const FINAL_QC_QUEUE_START = "2026-10-22T04:00:00.000Z";

/** A submitted inspection with nothing left open is waiting on final QC. */
export function awaitingFinalQc(input: {
  sessionStatus: string;
  summary: Record<string, unknown> | null | undefined;
  openFailures: number;
  completedAt?: string | null;
}): boolean {
  if (input.sessionStatus !== "COMPLETED") return false;
  if (input.openFailures > 0) return false;
  if (readQcCorrection(input.summary)) return false;
  if (readFinalQc(input.summary)) return false;
  const completedAt = (input.completedAt ?? "").trim();
  if (completedAt && completedAt < FINAL_QC_QUEUE_START) return false;
  return true;
}
